import { AnalyticsPostThumbnail as PostThumbnail } from "../components/AnalyticsPostThumbnail";
import { AnalyticsAccountIdentity, formatPlatform } from "../components/AnalyticsAccountIdentity";
import { useAnalyticsPostPages } from "../hooks/useAnalyticsPostPages";
import { VirtualizedPostGrid } from "../components/VirtualizedPostGrid";
import { AnalyticsCoverageInfo } from "../components/AnalyticsCoverageInfo";
import { AnalyticsDiscoveryNotice } from "../components/AnalyticsDiscoveryNotice";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  CalendarDays,
  Info,
  Loader2,
  RefreshCw,
} from "lucide-react";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { useAnalyticsOverview } from "../hooks/useAnalyticsOverview";
import { useAnalyticsUiEnabled } from "../hooks/useAnalyticsUiEnabled";
import { useWorkspaceConnections } from "../hooks/useWorkspaceConnections";
import {
  AnalyticsChannelRow,
  AnalyticsBackfillConnection,
  AnalyticsComparison,
  AnalyticsCoverage,
  AnalyticsReconnectReason,
  AnalyticsSetupConnection,
  AnalyticsSummaryMetric,
  AnalyticsTopPost,
  AnalyticsSort,
} from "../models/AnalyticsOverview";
import { PlatformConnection } from "../models/Platform";
import { Post } from "../models/Post";
import { db } from "../services/Firebase";
import { postConverter } from "../services/converters";
import { PlatformConnectionIcon } from "../components/PlatformConnectionIcon";
import { PostViewer } from "../components/PostViewer";
import { Button } from "../components/ui/button";
import { Portal as TooltipPortal } from "@radix-ui/react-tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { cn } from "../lib/utils";
import { beginWorkspaceOAuth } from "../lib/workspaceApi";
import { useToast } from "../components/ui/use-toast";

type RangeDays = 7 | 30 | 90;
type SummaryKey =
  | "published"
  | "visibility"
  | "engagements"
  | "engagementRate"
  | "followersGained";

const SUMMARY_DEFINITIONS: Array<{
  key: SummaryKey;
  label: string;
  description: string;
}> = [
  {
    key: "published",
    label: "Published",
    description: "The number of platform publications sent during this period.",
  },
  {
    key: "visibility",
    label: "Visibility",
    description:
      "Video views where available, otherwise impressions or reach. Unsupported publications are excluded.",
  },
  {
    key: "engagements",
    label: "Engagements",
    description:
      "Clicks, likes or reactions, comments, shares, and saves combined where available.",
  },
  {
    key: "engagementRate",
    label: "Engagement rate",
    description:
      "Engagements divided by visibility for publications reporting both values.",
  },
  {
    key: "followersGained",
    label: "Followers gained",
    description:
      "The net audience change across accounts with both a starting and ending follower snapshot.",
  },
];

const STATUS_LABELS: Record<
  Exclude<AnalyticsChannelRow["status"], "reporting">,
  string
> = {
  collecting: "Collecting",
  backfilling: "Backfilling",
  needs_reconnect: "Reconnect",
  limited: "Limited data",
  no_posts: "No posts",
};

export default function AnalyticsPage() {
  const analyticsEnabled = useAnalyticsUiEnabled();
  return analyticsEnabled ? (
    <AnalyticsPageContent />
  ) : (
    <Navigate to="/dashboard" replace />
  );
}

function AnalyticsPageContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeWorkspaceId, currentUser } = useAuth();
  const { toast } = useToast();
  const oauthReturnHandled = useRef(false);
  const oauthReturn = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      status: params.get("status"),
      platform: params.get("platform"),
      error: params.get("error"),
    };
  }, [location.search]);
  const [days, setDays] = useState<RangeDays>(30);
  const [selectedConnectionId, setSelectedConnectionId] = useState("all");
  const [sort, setSort] = useState<AnalyticsSort>("engagements");
  const [selectedPostForViewer, setSelectedPostForViewer] =
    useState<Post | null>(null);
  const [openingPostId, setOpeningPostId] = useState<string | null>(null);
  const [connectingConnectionId, setConnectingConnectionId] = useState<
    string | null
  >(null);
  const {
    connections,
    loading: connectionsLoading,
    error: connectionsError,
  } = useWorkspaceConnections();
  const connectionIds = useMemo(
    () => (selectedConnectionId === "all" ? [] : [selectedConnectionId]),
    [selectedConnectionId],
  );
  const connectionsById = useMemo<Record<string, PlatformConnection>>(
    () =>
      Object.fromEntries(
        connections.map((connection) => [connection.id, connection]),
      ),
    [connections],
  );
  const {
    data,
    loading,
    error,
    preparing,
    preparationError,
    prepareBackfill,
    refetch,
  } = useAnalyticsOverview({
    days,
    connectionIds,
    sort,
    forceFreshOnMount: oauthReturn.status === "success",
  });

  const postPages = useAnalyticsPostPages(
    data,
    connectionIds,
    `${days}:${sort}:${selectedConnectionId}`,
    loading,
  );

  useEffect(() => {
    if (!oauthReturn.status || oauthReturnHandled.current || loading) return;
    oauthReturnHandled.current = true;
    if (oauthReturn.status === "success") {
      const platform = oauthReturn.platform
        ? `${oauthReturn.platform[0].toUpperCase()}${oauthReturn.platform.slice(1)}`
        : "Account";
      toast({
        title: `${platform} reconnected`,
        description: "Your analytics access has been refreshed.",
      });
    } else {
      toast({
        title: "Reconnection was not completed",
        description: oauthReturn.error
          ? "Please try again and approve the requested permissions."
          : "Please try again.",
        variant: "destructive",
      });
    }
    navigate("/analytics", { replace: true });
  }, [loading, navigate, oauthReturn, toast]);

  useEffect(() => {
    if (
      selectedConnectionId !== "all" &&
      !connections.some((connection) => connection.id === selectedConnectionId)
    ) {
      setSelectedConnectionId("all");
    }
  }, [connections, selectedConnectionId]);

  const setupReady = data?.setup.status === "ready";
  const blockingReconnectConnections =
    data?.setup.reconnectConnections.filter(
      (connection) => connection.reason !== "follower_permission",
    ) ?? [];
  const reconnectGateRequired =
    data?.setup.status === "reconnect_required" &&
    blockingReconnectConnections.length > 0;
  const backfillingConnections = data?.setup.backfillingConnections ?? [];
  const isRefreshing = loading && Boolean(data);
  const noConnections =
    !connectionsLoading &&
    !connectionsError &&
    connections.length === 0 &&
    !loading &&
    (data?.selectedConnectionCount ?? 0) === 0;

  useEffect(() => {
    if (!setupReady) setSelectedPostForViewer(null);
  }, [setupReady]);

  const reconnectFromAnalytics = useCallback(
    async (connection: AnalyticsSetupConnection) => {
      if (!currentUser || !activeWorkspaceId || connectingConnectionId) return;
      setConnectingConnectionId(connection.connectionId);
      try {
        await beginWorkspaceOAuth(
          currentUser,
          activeWorkspaceId,
          connection.platform,
          {
            reconnectConnectionId: connection.connectionId,
            returnPath: "/analytics",
          },
        );
      } catch (connectError) {
        setConnectingConnectionId(null);
        toast({
          title: "Reconnection could not start",
          description:
            connectError instanceof Error
              ? connectError.message
              : "Please try again.",
          variant: "destructive",
        });
      }
    },
    [activeWorkspaceId, connectingConnectionId, currentUser, toast],
  );

  const openPostViewer = useCallback(
    async (postId: string) => {
      if (!activeWorkspaceId || openingPostId) return;
      setOpeningPostId(postId);
      try {
        const snapshot = await getDoc(
          doc(db, "posts", postId).withConverter(postConverter),
        );
        const post = snapshot.exists()
          ? { ...snapshot.data(), id: snapshot.id }
          : null;
        const belongsToWorkspace =
          post &&
          (post.userId === activeWorkspaceId ||
            post.workspaceId === activeWorkspaceId);
        if (!post || !belongsToWorkspace) {
          throw new Error(
            "This post is no longer available in the active workspace.",
          );
        }
        setSelectedPostForViewer(post);
      } catch (viewError) {
        toast({
          title: "Post could not be opened",
          description:
            viewError instanceof Error
              ? viewError.message
              : "Please try again.",
          variant: "destructive",
        });
      } finally {
        setOpeningPostId(null);
      }
    },
    [activeWorkspaceId, openingPostId, toast],
  );

  return (
    <TooltipProvider delayDuration={250}>
      <div className="mx-auto w-full max-w-[1280px] pb-12">
        <header className="flex flex-col gap-6 border-b border-border pb-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-brand-ink">
              <BarChart3 className="h-4 w-4" aria-hidden />
              Analytics
              <AnalyticsCoverageInfo connections={connections.filter(connection =>
                selectedConnectionId === "all" || connection.id === selectedConnectionId)} />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              See what is earning attention
            </h1>
            <p className="mt-2 max-w-[65ch] text-sm leading-6 text-muted-foreground">
              {data?.setup.status === "reconnect_required"
                ? "Complete account access before Wavegen prepares your analytics."
                : data?.setup.status === "backfilling"
                  ? "We’re collecting your historical post performance."
                  : "Compare published content and channel performance without switching between platforms."}
            </p>
          </div>

          {connections.length > 0 && (
            <div className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto lg:items-center">
              <Select
                value={selectedConnectionId}
                onValueChange={setSelectedConnectionId}
              >
                <SelectTrigger
                  className="h-11 w-full bg-background sm:w-[230px]"
                  aria-label="Filter analytics by channel"
                  disabled={connectionsLoading || connections.length === 0}
                >
                  <SelectValue placeholder="All channels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All channels</SelectItem>
                  {connections.map((connection) => (
                    <SelectItem key={connection.id} value={connection.id}>
                      {connection.profileName} ·{" "}
                      {formatPlatform(connection.platform)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div
                className="grid h-11 grid-cols-3 rounded-lg border border-input bg-background p-1"
                role="group"
                aria-label="Analytics date range"
              >
                {([7, 30, 90] as const).map((range) => (
                  <button
                    key={range}
                    type="button"
                    onClick={() => setDays(range)}
                    aria-pressed={days === range}
                    className={cn(
                      "min-w-[68px] rounded-md px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                      days === range
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    {range} days
                  </button>
                ))}
              </div>
            </div>
          )}
        </header>

        {data && <AnalyticsDiscoveryNotice
          connections={data.setup.discoveryConnections ?? []}
          preparing={preparing}
          backfilling={data.setup.status === "backfilling" && !reconnectGateRequired}
          connectingConnectionId={connectingConnectionId}
          onReconnect={reconnectFromAnalytics}
          error={preparationError}
          onRetry={() => void prepareBackfill()}
        />}

        {noConnections ? (
          <NoConnections onConnect={() => navigate("/settings")} />
        ) : error && !data ? (
          <AnalyticsError message={error.message} onRetry={refetch} />
        ) : loading && !data ? (
          <AnalyticsLoading />
        ) : reconnectGateRequired ? (
          <AnalyticsReconnectGate
            connections={blockingReconnectConnections}
            backfillingConnections={backfillingConnections}
            connectingConnectionId={connectingConnectionId}
            onReconnect={reconnectFromAnalytics}
          />
        ) : data?.setup.status === "backfilling" ? null : data ? (
          <div className="relative" aria-busy={loading}>
            {isRefreshing && (
              <div
                className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center"
                role="status"
                aria-live="polite"
              >
                <span className="rounded-full border border-border bg-background/95 p-3 text-brand-ink">
                  <Loader2
                    className="h-6 w-6 animate-spin motion-reduce:animate-[spin_1.8s_linear_infinite]"
                    aria-hidden
                  />
                  <span className="sr-only">Updating analytics…</span>
                </span>
              </div>
            )}
            <div
              className={cn(
                "transition-opacity duration-150 motion-reduce:transition-none",
                isRefreshing &&
                  "pointer-events-none select-none opacity-40 [&_.animate-spin]:animate-none",
              )}
            >
              {(data.setup.reconnectConnections.length > 0 ||
                data.setup.backfillingConnections.length > 0) && (
                <AnalyticsAccountNotice
                  included={data.includedConnectionCount}
                  total={data.selectedConnectionCount}
                  reconnectConnections={data.setup.reconnectConnections}
                  backfillingConnections={data.setup.backfillingConnections}
                  connectingConnectionId={connectingConnectionId}
                  error={preparationError}
                  onReconnect={reconnectFromAnalytics}
                  onRetry={() => void prepareBackfill()}
                />
              )}

              <section aria-labelledby="summary-heading" className="mt-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h2
                      id="summary-heading"
                      className="text-lg font-semibold text-foreground"
                    >
                      Performance summary
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Latest available totals for publications sent in this
                      period.
                    </p>
                    <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                      <span>{formatRange(data.range.from, data.range.to)}</span>
                      <span aria-hidden>·</span>
                      <span>
                        Compared with{" "}
                        {formatRange(
                          data.range.previousFrom,
                          data.range.previousTo,
                        )}
                      </span>
                    </p>
                  </div>
                  <AnalyticsFreshness
                    updatedAt={data.updatedAt}
                    updating={(data.setup.updatingConnectionIds ?? []).flatMap(
                      (id) => (connectionsById[id] ? [connectionsById[id]] : []),
                    )}
                  />
                </div>

                <div className="mt-5 grid grid-cols-2 overflow-hidden rounded-xl border border-border bg-background md:grid-cols-5">
                  {SUMMARY_DEFINITIONS.map((definition, index) => (
                    <SummaryItem
                      key={definition.key}
                      label={definition.label}
                      description={definition.description}
                      metric={data.summary[definition.key]}
                      metricKey={definition.key}
                      className={summaryBorderClass(index)}
                    />
                  ))}
                </div>
              </section>

              {data.summary.published.value === 0 ? (
                <NoPostsInRange onOpenCalendar={() => navigate("/calendar")} />
              ) : (
                <section aria-labelledby="top-posts-heading" className="mt-12">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <h2
                        id="top-posts-heading"
                        className="text-lg font-semibold text-foreground"
                      >
                        Top posts
                      </h2>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Posts in the selected period, with reported results ranked
                        first and pending results shown afterward.
                      </p>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <Select
                        value={sort}
                        onValueChange={(value) =>
                          setSort(value as AnalyticsSort)
                        }
                      >
                        <SelectTrigger
                          className="h-10 w-full bg-background sm:w-[190px]"
                          aria-label="Rank top posts by"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="engagements">
                            Most engagements
                          </SelectItem>
                          <SelectItem value="visibility">
                            Most visibility
                          </SelectItem>
                          <SelectItem value="engagementRate">
                            Highest engagement rate
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {postPages.posts.length ? (
                    <>
                      <VirtualizedPostGrid
                        items={postPages.posts}
                        itemKey={post => post.publicationId}
                        renderItem={(post, index) => (
                          <TopPostCard
                            key={post.publicationId}
                            post={post}
                            rank={index + 1}
                            isOpening={openingPostId === post.wavegenPostId}
                            onView={openPostViewer}
                          />
                        )}
                      />
                      <div className="mt-5 flex flex-col items-center gap-3">
                        <p className="text-sm text-muted-foreground" role="status">
                          Showing {postPages.posts.length} of {postPages.total} posts
                        </p>
                        {postPages.error && (
                          <p role="alert" className="text-sm text-destructive">
                            {postPages.error}
                          </p>
                        )}
                        {postPages.hasMore && (
                          <Button
                            variant="outline"
                            aria-controls="analytics-ranked-posts"
                            disabled={loading || postPages.loading}
                            onClick={() => void postPages.loadMore()}
                          >
                            {postPages.loading
                              ? "Loading more…"
                              : postPages.error ? "Try again" : "Show more"}
                          </Button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="mt-5 rounded-xl border border-border bg-background px-6 py-10 text-center">
                      <p className="font-medium text-foreground">
                        No ranked posts yet
                      </p>
                      <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">
                        Published posts are present, but the selected metric has
                        not been reported yet. Try another ranking or check back
                        after collection finishes.
                      </p>
                    </div>
                  )}
                </section>
              )}

              <section aria-labelledby="channels-heading" className="mt-12">
                <div>
                  <h2
                    id="channels-heading"
                    className="text-lg font-semibold text-foreground"
                  >
                    Channel performance
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Compare output, attention, engagement, and audience change
                    by account.
                  </p>
                </div>
                <ChannelPerformance
                  channels={data.channels}
                  onSelect={(connectionId) =>
                    setSelectedConnectionId(connectionId)
                  }
                />
              </section>

              <footer className="mt-10 border-t border-border pt-5 text-xs leading-5 text-muted-foreground">
                Missing or unsupported metrics are shown as an em dash and are
                never counted as zero. Visibility combines each platform's best
                available view, impression, or reach metric.
              </footer>
            </div>
          </div>
        ) : null}
      </div>
      {selectedPostForViewer && (
        <PostViewer
          isOpen
          onClose={() => setSelectedPostForViewer(null)}
          expandedPost={selectedPostForViewer}
          initialIndex={0}
          connectionsById={connectionsById}
        />
      )}
    </TooltipProvider>
  );
}

function SummaryItem({
  label,
  description,
  metric,
  metricKey,
  className,
}: {
  label: string;
  description: string;
  metric: AnalyticsSummaryMetric;
  metricKey: SummaryKey;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0 p-4 sm:p-5", className)}>
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <span>{label}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`About ${label}`}
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs leading-5">
            {description}
          </TooltipContent>
        </Tooltip>
      </div>
      <p className="mt-3 text-2xl font-semibold tracking-tight tabular-nums text-foreground">
        {formatSummaryValue(metricKey, metric.value)}
      </p>
      <Comparison comparison={metric.comparison} />
      <Coverage coverage={metric.coverage} metricKey={metricKey} />
    </div>
  );
}

export function AnalyticsFreshness({
  updatedAt,
  updating = [],
}: {
  updatedAt: number | null;
  updating?: PlatformConnection[];
}) {
  return (
    <div
      className="flex min-h-5 items-center gap-1.5 text-xs text-muted-foreground sm:justify-end"
      role="status"
      aria-live="polite"
    >
      {updating.length > 0 ? (
        <>
          <Loader2
            className="h-3.5 w-3.5 animate-spin text-brand-ink motion-reduce:animate-none"
            aria-hidden
          />
          <span>
            {updating.length === 1
              ? `Updating ${updating[0].profileName}…`
              : `Updating ${updating.length} accounts…`}
          </span>
        </>
      ) : updatedAt ? (
        <span>Updated {timeAgo(updatedAt)}</span>
      ) : null}
    </div>
  );
}

function Comparison({ comparison }: { comparison: AnalyticsComparison }) {
  if (comparison.kind === "unavailable") {
    return (
      <p className="mt-1.5 text-xs text-muted-foreground">
        No prior comparison
      </p>
    );
  }
  if (comparison.kind === "new") {
    return (
      <p className="mt-1.5 text-xs text-emerald-700">Up from 0 last period</p>
    );
  }
  if (comparison.value === null) return null;
  const isPositive = comparison.value > 0;
  const isNegative = comparison.value < 0;
  const Icon = isPositive ? ArrowUpRight : isNegative ? ArrowDownRight : null;
  const suffix =
    comparison.kind === "percent"
      ? "%"
      : comparison.kind === "points"
        ? " pts"
        : "";
  return (
    <p
      className={cn(
        "mt-1.5 flex items-center gap-1 text-xs font-medium",
        isPositive && "text-emerald-700",
        isNegative && "text-red-600",
        !isPositive && !isNegative && "text-muted-foreground",
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
      <span className="tabular-nums">
        {formatSigned(comparison.value, comparison.kind === "absolute" ? 0 : 1)}
        {suffix} vs previous
      </span>
    </p>
  );
}

function Coverage({
  coverage,
  metricKey,
}: {
  coverage: AnalyticsCoverage;
  metricKey: SummaryKey;
}) {
  if (
    metricKey === "published" ||
    coverage.eligible === 0 ||
    coverage.reported === coverage.eligible
  ) {
    return null;
  }
  return (
    <p className="mt-3 text-[11px] leading-4 text-muted-foreground">
      {metricKey === "followersGained"
        ? `Available for ${coverage.reported} of ${coverage.eligible} included accounts`
        : "Some included platforms do not provide this metric"}
    </p>
  );
}

const TopPostCard = memo(function TopPostCard({
  post,
  rank,
  onView,
  isOpening,
}: {
  post: AnalyticsTopPost;
  rank: number;
  onView: (postId: string) => void;
  isOpening: boolean;
}) {
  return (
    <article className="group flex min-w-0 flex-col overflow-hidden rounded-xl border border-border bg-background transition-colors hover:border-foreground/25">
      <PostThumbnail post={post} />
      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <PlatformConnectionIcon
              platform={post.platform}
              profileImage={post.profileImage}
              profileName={post.profileName}
              size="sm"
              disableTooltip
            />
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-foreground">
                {post.profileName}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {formatPlatform(post.platform)} ·{" "}
                {formatShortDate(post.publishedAt)}
              </p>
            </div>
          </div>
          <span
            className={cn(
              "text-xs font-semibold tabular-nums",
              post.rankingAvailable
                ? "text-brand-ink"
                : "text-muted-foreground",
            )}
          >
            {post.rankingAvailable ? `#${rank}` : "Awaiting data"}
          </span>
        </div>

        <p className="mt-4 line-clamp-3 min-h-[3.75rem] text-sm leading-5 text-foreground">
          {post.caption}
        </p>

        <dl className="mt-5 grid grid-cols-3 gap-3 border-t border-border pt-4">
          <PostMetric label="Visibility" value={formatCount(post.visibility)} />
          <PostMetric
            label="Engagements"
            value={formatCount(post.engagements)}
          />
          <PostMetric label="Rate" value={formatRate(post.engagementRate)} />
        </dl>

        {post.externalUrl ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <a href={post.externalUrl} target="_blank" rel="noopener noreferrer"
                className="mt-5 inline-flex min-h-11 items-center justify-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                View on {formatPlatform(post.platform)}
                <Info className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
              </a>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="top" align="end" className="w-72 max-w-[calc(100vw-2rem)] p-3 text-sm leading-5">
                This post wasn’t published through Wavegen. You can track its
                performance here and view the original on {formatPlatform(post.platform)}.
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        ) : <button
          type="button"
          onClick={() => void onView(post.wavegenPostId)}
          disabled={isOpening || !post.wavegenPostId}
          className="mt-5 inline-flex min-h-11 items-center justify-center rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {isOpening && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
          )}
          {isOpening ? "Opening…" : "View post"}
        </button>}
      </div>
    </article>
  );
});


function PostMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm font-semibold tabular-nums text-foreground">
        {value}
      </dd>
    </div>
  );
}

function ChannelPerformance({
  channels,
  onSelect,
}: {
  channels: AnalyticsChannelRow[];
  onSelect: (connectionId: string) => void;
}) {
  return (
    <div className="mt-5 overflow-hidden rounded-xl border border-border bg-background">
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-border bg-muted/60 text-xs font-medium text-muted-foreground">
              <th scope="col" className="px-4 py-3">
                Channel
              </th>
              <th scope="col" className="px-4 py-3 text-right">
                Published
              </th>
              <th scope="col" className="px-4 py-3 text-right">
                Visibility
              </th>
              <th scope="col" className="px-4 py-3 text-right">
                Engagements
              </th>
              <th scope="col" className="px-4 py-3 text-right">
                Eng. rate
              </th>
              <th scope="col" className="px-4 py-3 text-right">
                Followers
              </th>
            </tr>
          </thead>
          <tbody>
            {channels.map((channel) => (
              <tr
                key={channel.connectionId}
                className="border-b border-border last:border-0"
              >
                <th scope="row" className="px-4 py-4 font-normal">
                  <ChannelIdentity channel={channel} onSelect={onSelect} />
                </th>
                <ChannelCell value={String(channel.published)} />
                <ChannelCell value={formatCount(channel.visibility)} />
                <ChannelCell value={formatCount(channel.engagements)} />
                <ChannelCell value={formatRate(channel.engagementRate)} />
                <ChannelCell
                  value={formatFollowers(
                    channel.followers,
                    channel.followersGained,
                  )}
                />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="divide-y divide-border lg:hidden">
        {channels.map((channel) => (
          <div key={channel.connectionId} className="p-4">
            <ChannelIdentity channel={channel} onSelect={onSelect} />
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
              <MobileMetric
                label="Published"
                value={String(channel.published)}
              />
              <MobileMetric
                label="Visibility"
                value={formatCount(channel.visibility)}
              />
              <MobileMetric
                label="Engagements"
                value={formatCount(channel.engagements)}
              />
              <MobileMetric
                label="Engagement rate"
                value={formatRate(channel.engagementRate)}
              />
              <MobileMetric
                label="Followers"
                value={formatFollowers(
                  channel.followers,
                  channel.followersGained,
                )}
              />
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChannelIdentity({
  channel,
  onSelect,
}: {
  channel: AnalyticsChannelRow;
  onSelect: (connectionId: string) => void;
}) {
  return (
    <div className="flex min-w-[220px] items-center gap-3">
      <PlatformConnectionIcon
        platform={channel.platform}
        profileImage={channel.profileImage}
        profileName={channel.profileName}
        size="sm"
        disableTooltip
      />
      <div className="min-w-0">
        <button
          type="button"
          onClick={() => onSelect(channel.connectionId)}
          className="block max-w-[220px] truncate rounded-sm text-left text-sm font-medium text-foreground hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {channel.profileName}
        </button>
        <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>{formatPlatform(channel.platform)}</span>
          {channel.status !== "reporting" && (
            <>
              <span aria-hidden>·</span>
              <StatusLabel
                status={channel.status}
                reconnectReason={channel.reconnectReason}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusLabel({
  status,
  reconnectReason,
}: {
  status: AnalyticsChannelRow["status"];
  reconnectReason: AnalyticsReconnectReason | null;
}) {
  if (status === "reporting") return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          (status === "collecting" || status === "backfilling") &&
            "bg-amber-500",
          status === "needs_reconnect" && "bg-red-500",
          (status === "limited" || status === "no_posts") &&
            "bg-muted-foreground/45",
        )}
        aria-hidden
      />
      {status === "needs_reconnect" &&
      reconnectReason === "analytics_permission"
        ? "Analytics permission needed"
        : STATUS_LABELS[status]}
    </span>
  );
}

function ChannelCell({ value }: { value: string }) {
  return (
    <td className="px-4 py-4 text-right text-sm tabular-nums text-foreground">
      {value}
    </td>
  );
}

function MobileMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm font-semibold tabular-nums text-foreground">
        {value}
      </dd>
    </div>
  );
}

function AnalyticsAccountNotice({
  included,
  total,
  reconnectConnections,
  backfillingConnections,
  connectingConnectionId,
  error,
  onReconnect,
  onRetry,
}: {
  included: number;
  total: number;
  reconnectConnections: AnalyticsSetupConnection[];
  backfillingConnections: AnalyticsBackfillConnection[];
  connectingConnectionId: string | null;
  error: Error | null;
  onReconnect: (connection: AnalyticsSetupConnection) => void;
  onRetry: () => void;
}) {
  const followerPermissionOnly =
    included === total &&
    reconnectConnections.length > 0 &&
    reconnectConnections.every(
      (connection) => connection.reason === "follower_permission",
    );
  return (
    <section
      className="mt-6 overflow-hidden rounded-xl border border-amber-200 bg-[oklch(0.98_0.018_75)]"
      aria-labelledby="analytics-account-notice-heading"
    >
      <div className="flex gap-3 px-4 py-4 sm:px-5">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800">
          <AlertTriangle className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0">
          <h2
            id="analytics-account-notice-heading"
            className="text-sm font-semibold text-amber-950"
          >
            {followerPermissionOnly
              ? "Add follower analytics"
              : `Analytics includes ${included} of ${total} accounts`}
          </h2>
          <p className="mt-1 max-w-3xl text-sm leading-5 text-amber-900">
            {followerPermissionOnly
              ? reconnectConnections.length === 1
                ? "Reconnect the account below to add follower totals and growth."
                : "Reconnect the accounts below to add follower totals and growth."
              : analyticsAccountNoticeMessage(
                  reconnectConnections.length,
                  backfillingConnections.length,
                )}
          </p>
        </div>
      </div>

      <div className="divide-y divide-amber-200 border-t border-amber-200 bg-background">
        {reconnectConnections.map((connection) => {
          const isConnecting =
            connection.connectionId === connectingConnectionId;
          return (
            <div
              key={connection.connectionId}
              className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5"
            >
              <AnalyticsAccountIdentity
                profileName={connection.profileName}
                profileImage={connection.profileImage}
                platform={connection.platform}
                detail={
                  connection.reason === "analytics_permission"
                    ? "Analytics permission needed"
                    : connection.reason === "follower_permission"
                      ? "Follower analytics permission needed"
                      : "Connection expired"
                }
              />
              <Button
                variant="outline"
                size="sm"
                className="min-h-10 w-full bg-background sm:w-auto"
                disabled={connectingConnectionId !== null}
                onClick={() => onReconnect(connection)}
              >
                {isConnecting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                )}
                {isConnecting ? "Opening…" : "Reconnect account"}
              </Button>
            </div>
          );
        })}
        {backfillingConnections.map((connection) => (
          <div
            key={connection.connectionId}
            className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5"
          >
            <AnalyticsAccountIdentity
              profileName={connection.profileName}
              profileImage={connection.profileImage}
              platform={connection.platform}
              detail="Backfilling analytics"
            />
            <BackfillAccountActivity />
          </div>
        ))}
      </div>

      {error && (
        <div
          className="flex flex-col gap-3 border-t border-red-200 bg-red-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5"
          role="alert"
        >
          <p className="text-sm text-red-900">
            We couldn’t start adding analytics. {error.message}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 bg-background"
            onClick={onRetry}
          >
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
            Try again
          </Button>
        </div>
      )}
    </section>
  );
}


function analyticsAccountNoticeMessage(
  reconnectCount: number,
  backfillingCount: number,
): string {
  if (reconnectCount > 0 && backfillingCount > 0) {
    return "Reconnect the accounts that need attention. We’re backfilling the others.";
  }

  if (reconnectCount > 0) {
    return reconnectCount === 1
      ? "Reconnect the account below to restore its analytics."
      : "Reconnect the accounts below to restore their analytics.";
  }

  return "No action needed. You can check back later.";
}

function BackfillAccountActivity() {
  return (
    <Loader2
      className="h-4 w-4 shrink-0 animate-spin text-amber-700"
      aria-label="Collecting analytics"
    />
  );
}

function AnalyticsReconnectGate({
  connections,
  backfillingConnections,
  connectingConnectionId,
  onReconnect,
}: {
  connections: AnalyticsSetupConnection[];
  backfillingConnections: AnalyticsBackfillConnection[];
  connectingConnectionId: string | null;
  onReconnect: (connection: AnalyticsSetupConnection) => void;
}) {
  return (
    <section
      className="max-w-3xl py-10 sm:py-14"
      aria-labelledby="analytics-setup-heading"
    >
      <div className="overflow-hidden rounded-xl border border-border bg-background">
        <div className="p-5 sm:p-7">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-50 text-amber-700">
            <AlertTriangle className="h-4 w-4" aria-hidden />
          </div>
          <h2
            id="analytics-setup-heading"
            className="mt-5 text-xl font-semibold text-foreground"
          >
            Reconnect an account to see analytics
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
            Reconnect one of the accounts below. No action is needed for
            accounts still backfilling.
          </p>
        </div>

        <div className="divide-y divide-border border-t border-border">
          {connections.map((connection) => {
            const isConnecting =
              connectingConnectionId === connection.connectionId;
            return (
              <div
                key={connection.connectionId}
                className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <PlatformConnectionIcon
                    platform={connection.platform}
                    profileImage={connection.profileImage}
                    profileName={connection.profileName}
                    size="sm"
                    disableTooltip
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">
                      {connection.profileName}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {formatPlatform(connection.platform)} ·{" "}
                      {connection.reason === "analytics_permission"
                        ? "Analytics permission required"
                        : connection.reason === "follower_permission"
                          ? "Follower analytics permission required"
                          : "Connection expired"}
                    </p>
                  </div>
                </div>
                <Button
                  variant="brand"
                  className="w-full shrink-0 sm:w-auto"
                  disabled={connectingConnectionId !== null}
                  onClick={() => onReconnect(connection)}
                >
                  {isConnecting && (
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      aria-hidden
                    />
                  )}
                  {isConnecting ? "Opening…" : "Reconnect"}
                </Button>
              </div>
            );
          })}
          {backfillingConnections.map((connection) => (
            <div
              key={connection.connectionId}
              className="flex items-center justify-between gap-4 px-5 py-4 sm:px-7"
            >
              <AnalyticsAccountIdentity
                profileName={connection.profileName}
                profileImage={connection.profileImage}
                platform={connection.platform}
                detail="Backfilling analytics"
              />
              <BackfillAccountActivity />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


function NoConnections({ onConnect }: { onConnect: () => void }) {
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center py-24 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <BarChart3 className="h-5 w-5" aria-hidden />
      </div>
      <h2 className="mt-5 text-lg font-semibold text-foreground">
        Connect a channel to see analytics
      </h2>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        Once you connect a channel, we’ll start tracking post performance and
        audience growth.
      </p>
      <Button variant="brand" className="mt-6" onClick={onConnect}>
        Connect a channel
      </Button>
    </div>
  );
}

function NoPostsInRange({ onOpenCalendar }: { onOpenCalendar: () => void }) {
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center py-20 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <CalendarDays className="h-5 w-5" aria-hidden />
      </div>
      <h2 className="mt-5 text-lg font-semibold text-foreground">
        No posts published in this period
      </h2>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        Choose a longer date range or open the calendar to prepare your next
        publication.
      </p>
      <Button variant="outline" className="mt-6" onClick={onOpenCalendar}>
        Open calendar
      </Button>
    </div>
  );
}

function AnalyticsError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div
      className="mx-auto flex max-w-lg flex-col items-center py-24 text-center"
      role="alert"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-700">
        <AlertTriangle className="h-5 w-5" aria-hidden />
      </div>
      <h2 className="mt-5 text-lg font-semibold text-foreground">
        Analytics could not be loaded
      </h2>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">{message}</p>
      <Button variant="outline" className="mt-6" onClick={onRetry}>
        <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
        Try again
      </Button>
    </div>
  );
}

function AnalyticsLoading() {
  return (
    <div
      className="animate-pulse pt-8 motion-reduce:animate-none"
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">Loading analytics…</span>
      <div aria-hidden>
        <div className="flex items-end justify-between gap-6">
          <div className="space-y-3">
            <div className="h-5 w-44 rounded bg-muted-foreground/15" />
            <div className="h-3 w-72 max-w-full rounded bg-muted-foreground/10" />
            <div className="h-3 w-56 max-w-full rounded bg-muted-foreground/10" />
          </div>
          <div className="hidden h-3 w-24 rounded bg-muted-foreground/10 sm:block" />
        </div>

        <div className="mt-5 grid grid-cols-2 overflow-hidden rounded-xl border border-border bg-background md:grid-cols-5">
          {[0, 1, 2, 3, 4].map((index) => (
            <div key={index} className={cn("p-5", summaryBorderClass(index))}>
              <div className="h-3 w-20 rounded bg-muted-foreground/10" />
              <div className="mt-4 h-7 w-16 rounded bg-muted-foreground/15" />
              <div className="mt-3 h-3 w-28 max-w-full rounded bg-muted-foreground/10" />
            </div>
          ))}
        </div>

        <div className="mt-12 flex items-end justify-between gap-6">
          <div className="space-y-3">
            <div className="h-5 w-24 rounded bg-muted-foreground/15" />
            <div className="h-3 w-80 max-w-full rounded bg-muted-foreground/10" />
          </div>
          <div className="hidden h-10 w-44 rounded-lg bg-muted-foreground/10 sm:block" />
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className="overflow-hidden rounded-xl border border-border bg-background"
            >
              <div className="aspect-[16/10] bg-muted-foreground/[0.08]" />
              <div className="space-y-4 p-4">
                <div className="h-7 w-36 rounded bg-muted-foreground/10" />
                <div className="space-y-2">
                  <div className="h-3 w-full rounded bg-muted-foreground/10" />
                  <div className="h-3 w-4/5 rounded bg-muted-foreground/10" />
                </div>
                <div className="h-12 rounded border-t border-border pt-4">
                  <div className="h-4 w-2/3 rounded bg-muted-foreground/10" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 space-y-3">
          <div className="h-5 w-40 rounded bg-muted-foreground/15" />
          <div className="h-3 w-96 max-w-full rounded bg-muted-foreground/10" />
        </div>
        <div className="mt-5 overflow-hidden rounded-xl border border-border bg-background">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className="flex items-center justify-between gap-8 border-b border-border px-4 py-5 last:border-b-0"
            >
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-muted-foreground/10" />
                <div className="space-y-2">
                  <div className="h-3 w-28 rounded bg-muted-foreground/15" />
                  <div className="h-3 w-20 rounded bg-muted-foreground/10" />
                </div>
              </div>
              <div className="hidden gap-10 sm:flex">
                {[0, 1, 2, 3].map((cell) => (
                  <div
                    key={cell}
                    className="h-4 w-12 rounded bg-muted-foreground/10"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function summaryBorderClass(index: number): string {
  const mobile =
    index < 4
      ? index % 2 === 0
        ? "border-b border-r"
        : "border-b"
      : "col-span-2";
  const desktop =
    index < 4 ? "md:col-span-1 md:border-b-0 md:border-r" : "md:col-span-1";
  return `${mobile} ${desktop}`;
}

function formatSummaryValue(key: SummaryKey, value: number | null): string {
  if (value == null) return "—";
  if (key === "engagementRate") return `${formatDecimal(value)}%`;
  if (key === "followersGained") return formatSignedCompact(value);
  return formatCompact(value);
}

function formatCount(value: number | null): string {
  return value == null ? "—" : formatCompact(value);
}

function formatCompact(value: number): string {
  return new Intl.NumberFormat(undefined, {
    notation: Math.abs(value) >= 1_000 ? "compact" : "standard",
    maximumFractionDigits: Math.abs(value) >= 1_000 ? 1 : 0,
  }).format(value);
}

function formatRate(value: number | null): string {
  return value == null ? "—" : `${formatDecimal(value)}%`;
}

function formatDecimal(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(
    value,
  );
}

function formatSigned(value: number, fractionDigits: number): string {
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(Math.abs(value));
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `−${formatted}`;
  return formatted;
}

function formatSignedCompact(value: number): string {
  const formatted = formatCompact(Math.abs(value));
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `−${formatted}`;
  return formatted;
}

function formatFollowers(
  followers: number | null,
  gained: number | null,
): string {
  if (followers == null) return "—";
  return gained == null
    ? formatCompact(followers)
    : `${formatCompact(followers)} (${formatSignedCompact(gained)})`;
}


function formatRange(from: number, to: number): string {
  const formatter = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  });
  const end = new Date(to - 1);
  return `${formatter.format(new Date(from))}–${formatter.format(end)}`;
}

function formatShortDate(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(new Date(timestamp));
}

function timeAgo(timestamp: number): string {
  const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
